import { useState, useCallback } from "react";
import { Flex, Text, Button, toast } from "@jsq/ds/core";
import { Colors, Sizes } from "@jsq/ds/styles";
import { useAssignReviewerMutation } from "~/graphql/supergraphHooks";
import { AssignReviewerModal } from "./AssignReviewerModal";
import { EumApproverPickerModal } from "./EumApproverPickerModal";
import type { BillingCaseRowItem, CaseStatus } from "../types";
import type { EumApproverPickerSubmitArgs } from "./EumApproverPickerModal/types";

export type MakerFooterProps = {
  billingCase: BillingCaseRowItem;
  onStatusChange: (status: CaseStatus) => void;
  isDraft?: boolean;
  hasLocalChanges?: boolean;
  hasPendingEumChanges?: boolean;
  onSubmitForReview?: (reviewerId: number) => Promise<void>;
  onSubmitEumForReview?: (args: EumApproverPickerSubmitArgs) => Promise<void>;
};

export const MakerFooter = ({
  billingCase,
  onStatusChange,
  isDraft = false,
  hasLocalChanges = false,
  hasPendingEumChanges = false,
  onSubmitForReview,
  onSubmitEumForReview,
}: MakerFooterProps) => {
  const [isReviewerModalOpen, setIsReviewerModalOpen] = useState(false);
  const [isEumApproverModalOpen, setIsEumApproverModalOpen] = useState(false);

  const [assignReviewer, { loading: isLoading }] = useAssignReviewerMutation({
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleSubmitForReview = useCallback(
    async (reviewerId: number) => {
      if (onSubmitForReview) {
        await onSubmitForReview(reviewerId);
        setIsReviewerModalOpen(false);
        return;
      }

      const result = await assignReviewer({
        variables: {
          changeRequestId: billingCase.changeRequestId,
          reviewerId,
        },
      });
      const changeRequest = result.data?.assignReviewer?.changeRequest;
      if (changeRequest?.status) {
        onStatusChange(changeRequest.status);
      }
      setIsReviewerModalOpen(false);
    },
    [onSubmitForReview, assignReviewer, billingCase.changeRequestId, onStatusChange],
  );

  const handleSubmitEumForReview = useCallback(
    async (args: EumApproverPickerSubmitArgs) => {
      if (onSubmitEumForReview) {
        await onSubmitEumForReview(args);
      }
    },
    [onSubmitEumForReview],
  );

  const handleSubmitClick = useCallback(() => {
    if (hasPendingEumChanges) {
      setIsEumApproverModalOpen(true);
      return;
    }
    setIsReviewerModalOpen(true);
  }, [hasPendingEumChanges]);

  const isSubmitDisabled = (isDraft && !hasLocalChanges) || isLoading;

  const assigneeDisplay =
    billingCase.assignee && billingCase.assignee !== "Unassigned"
      ? billingCase.assignee
      : "Unassigned";

  return (
    <>
      <Flex
        alignItems="center"
        justifyContent={isDraft ? "flex-end" : "space-between"}
        gap={Sizes.MD}
      >
        {!isDraft && (
          <Text variant="secondary-sm" color={Colors.Neutral600}>
            Assigned to{" "}
            <Text variant="regular-md" asSpan color={Colors.Primary600}>
              {assigneeDisplay}
            </Text>
          </Text>
        )}
        <Button variant="primary" onClick={handleSubmitClick} disabled={isSubmitDisabled}>
          Submit for review
        </Button>
      </Flex>
      <AssignReviewerModal
        isOpen={isReviewerModalOpen}
        onClose={() => setIsReviewerModalOpen(false)}
        onSubmit={handleSubmitForReview}
      />
      <EumApproverPickerModal
        isOpen={isEumApproverModalOpen}
        onClose={() => setIsEumApproverModalOpen(false)}
        onSubmit={handleSubmitEumForReview}
      />
    </>
  );
};
